import { createTheme } from '@mui/material/styles';

/**
 * Paleta do NaPorta.club.
 *
 * A escolha do roxo profundo com acento dourado busca o aspecto de "clube":
 * roxo remete a exclusividade e pertencimento, o dourado marca a camada
 * comercial (ofertas, vouchers, planos) sem parecer liquidacao. O verde entra
 * apenas como sinal de confirmacao.
 *
 * A landing usa a variante escura para criar contraste com o app, que e claro.
 */
export const brand = {
  purple900: '#1A0430',
  purple800: '#2A0A4A',
  purple700: '#3D1063',
  purple500: '#6C2BD9',
  purple300: '#A97BF7',
  purple100: '#EDE3FF',
  gold: '#F5B93B',
  goldDark: '#C98A12',
  mint: '#2BD99B',
  ink: '#120A1C',
};

export const theme = createTheme({
  palette: {
    mode: 'dark',
    primary: { main: brand.purple500, light: brand.purple300, dark: brand.purple700, contrastText: '#FFFFFF' },
    secondary: { main: brand.gold, dark: brand.goldDark, contrastText: brand.purple900 },
    success: { main: brand.mint },
    background: { default: brand.purple900, paper: 'rgba(255,255,255,0.04)' },
    text: { primary: '#F6F2FF', secondary: 'rgba(246,242,255,0.68)' },
    divider: 'rgba(169,123,247,0.18)',
  },
  typography: {
    fontFamily: '"Plus Jakarta Sans", "Segoe UI", Roboto, Helvetica, Arial, sans-serif',
    h1: { fontWeight: 800, letterSpacing: '-0.03em', lineHeight: 1.08 },
    h2: { fontWeight: 800, letterSpacing: '-0.02em' },
    h3: { fontWeight: 700, letterSpacing: '-0.02em' },
    h4: { fontWeight: 700 },
    h5: { fontWeight: 700 },
    h6: { fontWeight: 600 },
    button: { fontWeight: 700, textTransform: 'none' },
    subtitle1: { lineHeight: 1.65 },
    body1: { lineHeight: 1.7 },
  },
  shape: { borderRadius: 16 },
  /**
   * Durações mais longas que o padrão do MUI (150-375 ms), pelo mesmo motivo
   * do tema do app: em tela cheia o padrão parece um piscar.
   */
  transitions: {
    duration: {
      shortest: 250,
      shorter: 320,
      short: 400,
      standard: 500,
      complex: 650,
      enteringScreen: 400,
      leavingScreen: 350,
    },
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          backgroundColor: brand.purple900,
          backgroundImage: `radial-gradient(1000px 520px at 12% -8%, ${brand.purple700} 0%, transparent 62%),
             radial-gradient(760px 420px at 92% 4%, rgba(108,43,217,0.42) 0%, transparent 58%)`,
          backgroundAttachment: 'fixed',
        },
        // Respeita quem pediu menos animacao no sistema operacional.
        '@media (prefers-reduced-motion: reduce)': {
          '*': { animationDuration: '0.01ms !important', transitionDuration: '0.01ms !important' },
          // Indicadores de progresso continuam girando (1s): um spinner parado
          // parece tela travada, e o movimento e o proprio feedback de estado.
          '.MuiCircularProgress-root, .MuiCircularProgress-svg, .MuiCircularProgress-circle, .MuiLinearProgress-bar':
            { animationDuration: '1s !important' },
        },
      },
    },
    /** Pulso do skeleton: 1,5 s padrão vira 2,4 s. */
    MuiSkeleton: {
      defaultProps: { animation: 'wave' },
      styleOverrides: {
        root: { '&::after': { animationDuration: '2.4s' } },
        pulse: { animationDuration: '2.4s' },
      },
    },
    /** Giro do spinner: 1,4 s padrão vira 2,2 s. */
    MuiCircularProgress: {
      styleOverrides: {
        indeterminate: { animationDuration: '2.2s' },
        circleIndeterminate: { animationDuration: '2.2s' },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: { borderRadius: 999, paddingInline: 22, paddingBlock: 11 },
        containedSecondary: { color: brand.purple900 },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          backgroundImage: 'none',
          border: '1px solid rgba(169,123,247,0.20)',
          backdropFilter: 'blur(8px)',
        },
      },
    },
    MuiChip: { styleOverrides: { root: { fontWeight: 600 } } },
    MuiLink: { defaultProps: { underline: 'hover' } },
  },
});

export default theme;
